"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { login, setAuthToken } from "@/lib/service/auth.service";
import type {
  ApiEnvelope,
  ApiUser,
  LoginResponse,
} from "@/lib/service/auth.service";

type LoginReturn = LoginResponse | ApiEnvelope;

/** Normalize both shapes (axios-wrapped vs plain) */
function extractUser(res: LoginReturn): { user?: ApiUser; message?: string } {
  if ("data" in res && res?.data && typeof res.data === "object") {
    const env = res.data as ApiEnvelope;
    return { user: env.data, message: env.message };
  }
  const env = res as ApiEnvelope;
  return { user: env.data, message: env.message };
}

export default function AdminSignIn() {
  const router = useRouter();

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      toast.error("All fields are required");
      return;
    }
    const isEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.email);
    if (!isEmail) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    const tid = toast.loading("Signing in...");

    try {
      const res = (await login({
        email: formData.email,
        password: formData.password,
      })) as LoginReturn;

      const { user } = extractUser(res);
      if (!user?.accessToken) throw new Error("Invalid login response");

      if (user.role !== "admin") {
        toast.error("Only admins can sign in here.", { id: tid });
        setIsLoading(false);
        return;
      }

      setAuthToken(user.accessToken);
      try {
        localStorage.setItem("accessToken", user.accessToken);
        if (user.refreshToken)
          localStorage.setItem("refreshToken", user.refreshToken);
      } catch {
        /* ignore */
      }

      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("authChanged"));
      }

      toast.success("Welcome, Admin!", { id: tid });
      router.push("/admin/orders");
    } catch (err) {
      const msg =
        (err as { message?: string })?.message ||
        "Unable to connect to the server";
      toast.error(msg, { id: tid });
      setIsLoading(false);
    }
  };

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden mt-[200px]"
      style={
        {
          "--brand-bg": "#0B0E12",
          "--brand-bg-soft": "#0F141A",
          "--brand-gold": "#D6B05E",
          "--brand-gold-strong": "#C99A2E",
          "--brand-cream": "#F5EFE3",
          "--brand-card": "rgba(18,22,28,0.7)",
          "--brand-stroke": "rgba(214,176,94,0.25)",
        } as React.CSSProperties
      }
    >
      <div className="absolute inset-0 bg-[var(--brand-bg)] " />
      <div
        className="absolute inset-0 opacity-10 "
        style={{
          backgroundImage:
            "radial-gradient(1200px 600px at 70% 10%, rgba(214,176,94,0.30), transparent 60%), radial-gradient(700px 400px at 20% 80%, rgba(214,176,94,0.22), transparent 60%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-20 mix-blend-soft-light"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.25'/%3E%3C/svg%3E\")",
        }}
      />

      <div className="relative z-10 flex items-center justify-center py-10">
        <div className="flex items-center gap-3">
          <span className="text-xl tracking-[0.2em] text-[var(--brand-cream)]">
            <strong className="font-semibold">ALMAJLIS</strong>{" "}
            <span className="text-[var(--brand-gold)]">ARABIA</span>
          </span>
        </div>
      </div>

      <div className="relative z-10 flex items-center justify-center px-4 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="w-full max-w-md rounded-2xl border bg-[var(--brand-card)] backdrop-blur-xl shadow-[0_10px_60px_rgba(0,0,0,0.35)]"
          style={{ borderColor: "var(--brand-stroke)" }}
        >
          <div className="px-8 pt-8 pb-4 text-center">
            <h1 className="text-2xl font-semibold tracking-tight text-[var(--brand-cream)]">
              Admin Sign In
            </h1>
            <p className="mt-2 text-sm text-slate-300/80">
              Private portal •{" "}
              <span className="text-[var(--brand-gold)]">Authorized only</span>
            </p>
          </div>

          <div
            className="mx-8 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(214,176,94,0.5), transparent)",
            }}
          />

          <form onSubmit={handleSubmit} className="px-8 py-6 space-y-5">
            <div className="space-y-1.5">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-[var(--brand-cream)]/90"
              >
                Admin Email
              </label>
              <div className="relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="block w-full rounded-lg border bg-[#0C1116]/70 px-4 py-2.5 text-[var(--brand-cream)] placeholder:text-slate-400/60 shadow-inner outline-none transition focus:ring-2"
                  style={{
                    borderColor: "rgba(214,176,94,0.25)",
                    boxShadow:
                      "inset 0 1px 0 rgba(255,255,255,0.03), 0 0 0 0 rgba(214,176,94,0)",
                  }}
                  placeholder="admin@example.com"
                  onFocus={(e) =>
                    (e.currentTarget.style.boxShadow =
                      "inset 0 1px 0 rgba(255,255,255,0.03), 0 0 0 3px rgba(214,176,94,0.25)")
                  }
                  onBlur={(e) =>
                    (e.currentTarget.style.boxShadow =
                      "inset 0 1px 0 rgba(255,255,255,0.03), 0 0 0 0 rgba(214,176,94,0)")
                  }
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-[var(--brand-cream)]/90"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="block w-full rounded-lg border bg-[#0C1116]/70 px-4 py-2.5 text-[var(--brand-cream)] placeholder:text-slate-400/60 shadow-inner outline-none transition focus:ring-2"
                  style={{
                    borderColor: "rgba(214,176,94,0.25)",
                    boxShadow:
                      "inset 0 1px 0 rgba(255,255,255,0.03), 0 0 0 0 rgba(214,176,94,0)",
                  }}
                  placeholder="••••••••"
                  onFocus={(e) =>
                    (e.currentTarget.style.boxShadow =
                      "inset 0 1px 0 rgba(255,255,255,0.03), 0 0 0 3px rgba(214,176,94,0.25)")
                  }
                  onBlur={(e) =>
                    (e.currentTarget.style.boxShadow =
                      "inset 0 1px 0 rgba(255,255,255,0.03), 0 0 0 0 rgba(214,176,94,0)")
                  }
                />
                <div className="mt-2 text-right">
                  <Link
                    href="/auth/forgot-password"
                    className="text-xs font-medium text-[var(--brand-gold)] hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="group relative mt-2 w-full overflow-hidden rounded-xl px-4 py-2.5 text-sm font-semibold text-[#121519] transition focus:outline-none disabled:opacity-60"
              style={{
                background:
                  "linear-gradient(180deg, var(--brand-gold), var(--brand-gold-strong))",
                boxShadow:
                  "0 8px 24px rgba(214,176,94,0.25), inset 0 1px 0 rgba(255,255,255,0.2)",
              }}
            >
              <span className="relative z-10">
                {isLoading ? "Signing in..." : "Sign in"}
              </span>
              <span className="pointer-events-none absolute inset-0 -translate-x-full bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.35),transparent)] transition group-hover:translate-x-full" />
            </button>
          </form>
        </motion.div>
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0B0E12] to-transparent" />
    </div>
  );
}
